// Quién le está echando agua a la leche.
//
// El acopiador lo hace de cabeza: si el total del día rindió por debajo, se
// pone a pensar qué fincas entregaron y cuál viene fallando. Esta cuenta es esa
// misma, hecha sobre lo que ya está anotado.
//
// ── Lo que NO se le pide al usuario ──
//
// Hace falta saber de qué fincas salió la leche de cada cuajada, y la cuajada
// no lo guarda. Se podría pedir que lo anote — y sería un campo más que llenar
// todos los días, en un programa cuyo comprador dijo «son personas de finca,
// entre más fácil mejor».
//
// Así que se deduce: la leche recibida ESE DÍA es la que se cuajó ese día. Es
// como funciona un ruta y es lo que el programa ya asume para
// calcular el precio de la leche de la cuajada. Cero trabajo nuevo.
//
// La deducción tiene un límite y hay que decirlo: si un día entraron cuatro
// fincas, las cuatro cargan con el resultado de esa cuajada. Por eso esto NO
// acusa a nadie — ordena sospechosos. El que aparece en casi todas las malas y
// en pocas buenas se señala solo, y ahí es donde vale la pena ir con el
// lactodensímetro.

const soloFecha = (f) => String(f || '').slice(0, 10);

// Cuánto se desvió una cuajada de lo que debía rendir, en tanto por uno.
// Positivo = gastó más leche de la cuenta.
function desvio(cuajada, esperadoDe) {
  const esperado = esperadoDe(cuajada);
  const litros = Number(cuajada.litros) || 0;
  const kilos = Number(cuajada.kilos) || 0;
  if (!esperado || esperado <= 0 || litros <= 0 || kilos <= 0) return null;
  return (litros / kilos - esperado) / esperado;
}

// Más de un 5% peor que lo normal ya es señal. Por debajo es el ruido de
// siempre entre una cuajada y otra — el mismo umbral que usa la pantalla de
// Producción, a propósito: dos definiciones de «rindió mal» en el mismo
// programa es como terminan diciendo cosas distintas.
export const MARGEN = 0.05;

// Las fincas que entregaron el día de una cuajada, con su parte.
export function fincasDelDia(recibos, fecha) {
  const dia = soloFecha(fecha);
  const suyos = (recibos || []).filter(r => r && soloFecha(r.fecha) === dia);
  const total = suyos.reduce((s, r) => s + (Number(r.litros) || 0), 0);
  if (total <= 0) return [];
  return suyos
    .map(r => ({
      finca_id: r.finca_id,
      finca: r.finca || r.nombre,
      litros: Number(r.litros) || 0,
      parte: Math.round((Number(r.litros) || 0) / total * 100),
    }))
    .sort((a, b) => b.litros - a.litros);
}

// El historial: por cada finca, en cuántas de las cuajadas de sus días se
// rindió mal.
//
// `esperadoDe(cuajada)` lo da quien llama, porque el rendimiento normal vive en
// la variedad y esta función no tiene por qué saber de productos.
export function sospechosos(cuajadas, recibos, esperadoDe) {
  // Cada cuajada juzgable, con quién entregó ese día.
  //
  // Hay DOS clases y no valen lo mismo:
  //
  //   la PRUEBA   la cuajada se hizo con la leche de una finca sola, porque el
  //               acopiador la aisló para medirla. No admite discusión.
  //   lo DEDUCIDO la cuajada fue de la mezcla del día, y solo se sabe quiénes
  //               entregaron. Es un indicio, no una prueba.
  const dias = [];
  const pruebas = [];
  for (const c of cuajadas || []) {
    const d = desvio(c, esperadoDe);
    if (d === null) continue;                 // sin rendimiento pactado no se juzga
    if (c.finca_id) {
      pruebas.push({ finca_id: c.finca_id, desvio: d, malo: d > MARGEN });
      continue;   // no entra en la deducción: sería contarla dos veces
    }
    dias.push({ desvio: d, malo: d > MARGEN, fincas: fincasDelDia(recibos, c.fecha) });
  }

  const porFinca = new Map();
  // Las fincas que solo aparecen en pruebas también tienen que salir en la
  // lista: son justamente las que se fueron a medir.
  for (const p of pruebas) {
    if (!porFinca.has(p.finca_id)) {
      porFinca.set(p.finca_id, { finca_id: p.finca_id, finca: null,
                                 dias: 0, malos: 0, litros: 0 });
    }
  }
  for (const dia of dias) {
    for (const f of dia.fincas) {
      const a = porFinca.get(f.finca_id) ||
        { finca_id: f.finca_id, finca: f.finca, dias: 0, malos: 0, litros: 0 };
      a.dias += 1;
      if (dia.malo) a.malos += 1;
      a.litros += f.litros;
      porFinca.set(f.finca_id, a);
    }
  }

  const media = (lista) => lista.length
    ? lista.reduce((s, x) => s + x, 0) / lista.length : null;

  return [...porFinca.values()]
    .map(a => {
      // LA CUENTA QUE IMPORTA, y no es «cuánto arrastra».
      //
      // La primera versión ponderaba el desvío por la parte de leche que puso
      // cada finca, y salía al revés: la que entrega el 70% todos los días
      // encabezaba la lista aunque estuviera también en todas las cuajadas
      // buenas. Ponderar por volumen señala al que más vende, no al que echa
      // agua.
      //
      // La pregunta de verdad es la que hace el acopiador de cabeza: ¿rinde
      // peor cuando entra esta finca que cuando no entra? Eso es un contraste,
      // no un promedio.
      const con = media(dias.filter(d => d.fincas.some(f => f.finca_id === a.finca_id))
                            .map(d => d.desvio));
      const sin = media(dias.filter(d => !d.fincas.some(f => f.finca_id === a.finca_id))
                            .map(d => d.desvio));

      // Si entrega TODOS los días no hay con qué comparar, y eso se dice en vez
      // de inventarlo: cuando una sola finca está en todas las cuajadas, no se
      // puede separar «la leche viene mala» de «el proceso viene mal».
      const contraste = (con !== null && sin !== null)
        ? Math.round((con - sin) * 1000) / 10 : null;

      // Lo medido directamente: las cuajadas hechas solo con su leche.
      const suyas = pruebas.filter(p => p.finca_id === a.finca_id);
      const desvioPrueba = suyas.length
        ? Math.round(media(suyas.map(p => p.desvio)) * 1000) / 10 : null;

      return {
        ...a,
        porcentajeMalos: a.dias > 0 ? Math.round(a.malos / a.dias * 100) : 0,
        // Cuánto peor rinde el día que ella entrega, en puntos porcentuales.
        contraste,
        // Sin días sin ella, no hay comparación posible. La pantalla lo dice.
        comparable: contraste !== null,
        // Lo medido aislando su leche. Es lo que no admite discusión.
        pruebas: suyas.length,
        pruebasMalas: suyas.filter(p => p.malo).length,
        desvioPrueba,
      };
    })
    // Primero lo MEDIDO, después lo deducido. Una finca con pruebas encima va
    // antes que otra con un indicio mayor: el indicio se discute, la prueba no.
    // Los no comparables y sin pruebas van al final — no se les puede acusar ni
    // absolver.
    .sort((a, b) =>
      (b.desvioPrueba ?? -Infinity) - (a.desvioPrueba ?? -Infinity) ||
      (b.contraste ?? -Infinity) - (a.contraste ?? -Infinity) ||
      b.litros - a.litros);
}

// Cuántos días hacen falta para que la señal signifique algo.
//
// Con dos entregas, «el 100% de sus días rindió mal» son dos días — y puede ser
// que ese día se cortó la leche por el calor. Señalar a alguien con eso es la
// clase de error que cuesta un proveedor.
export const DIAS_PARA_CREER = 4;

export function esSospechoso(finca) {
  if (!finca) return false;

  // Con una prueba basta para señalarla: esa cuajada se hizo con su leche y con
  // la de nadie más. Pedirle cuatro días a algo que ya se midió aislado sería
  // desconfiar de la única evidencia buena que hay.
  if (finca.pruebas > 0 && finca.desvioPrueba > MARGEN * 100) return true;

  // Sin prueba, hace falta el indicio completo.
  return finca.dias >= DIAS_PARA_CREER
    && finca.comparable
    && finca.porcentajeMalos >= 60
    // Que rinda al menos un 5% peor cuando ella entra. Sin este renglón,
    // cualquier diferencia mínima señalaría a alguien.
    && finca.contraste > MARGEN * 100;
}
